import classNames from 'classnames'
import React, { Component } from 'react'

import AffirmMarketing from '~/components/affirm-marketing'
import Amount from '~/components/amount'
import ConsentCheckbox from '~/components/consent-checkbox'
import ContentBlock from '~/components/content-block'
import ReCaptcha from '~/components/re-captcha'
import ResponsiveButton from '~/components/responsive-button'
import { i18n } from '~/i18n'

import {
  ViewCartButton,
  Disclaimer,
  StyledSummitSupportSummary,
} from './order-summary.styles'

import type { Props } from './order-summary.types'

import './order-summary.scss'

export default class OrderSummary extends Component<Props> {
  creditAmount() {
    // Unfortunately, orders and carts use different names for credit.
    // Use appliedCreditInCurrency if present
    return (
      this.props.summarizable.appliedCreditInCurrency ||
      this.props.summarizable.totalCreditApplied
    )
  }

  renderLine({ className, description, price, text, key }: any) {
    if (typeof price === 'number') {
      const textEl = text ? <div className="text">{text}</div> : null
      return (
        <div className={`row ${className}`} key={key} data-testid={key}>
          <div className="column left">
            <div className="description">{description}</div>
            {textEl}
          </div>
          <div className="column left price">
            <Amount value={price} />
          </div>
        </div>
      )
    }
  }

  renderSubtotal() {
    const subtotalText = i18n.t('pages.cart.subtotal_text')
    return this.renderLine({
      className: 'subtotal',
      description: `${subtotalText}:`,
      price: this.props.summarizable.subtotal,
      showNumberForZero: true,
    })
  }

  renderSavingsAmount() {
    const savingsText = i18n.t('pages.cart.savings_text')
    const { savings } = this.props.summarizable
    if (savings == null || savings <= 0) return
    return this.renderLine({
      className: 'savings',
      description: `${savingsText}:`,
      price: -savings,
      showNumberForZero: true,
    })
  }

  renderTaxesAndFees() {
    const { taxes } = this.props.summarizable
    return taxes?.map(({ key, amount, displayName }) =>
      this.renderLine({
        key,
        className: 'taxes-and-fees',
        description: `${displayName}:`,
        price: amount,
        showNumberForZero: true,
      }),
    )
  }

  renderFoundationDonation() {
    const donationText = i18n.t('pages.cart.donation_text')
    const { foundationDonation } = this.props.summarizable
    if (foundationDonation == 0) return
    return this.renderLine({
      className: 'foundation-donation',
      description: `${donationText}:`,
      price: foundationDonation,
    })
  }

  renderNetTotalAndCreditValue() {
    if (!this.props.adventureAssuranceCreditEnabled) return
    const creditAmount = this.creditAmount()

    if (creditAmount == null) return
    if (creditAmount <= 0) return
    return (
      <>
        {this.renderNetTotal()}
        {this.renderCreditAmount()}
      </>
    )
  }

  renderNetTotal() {
    const netTotalText = i18n.t('pages.cart.net_total_text')
    const description = <span>{netTotalText}</span>
    return this.renderLine({
      className: 'net-total',
      description,
      price: this.props.summarizable.netTotal,
      text: undefined,
      showNumberForZero: true,
    })
  }

  renderCreditAmount() {
    const creditAmount = this.creditAmount()
    if (!creditAmount) return
    const creditText = i18n.t('pages.cart.credit_text')

    return this.renderLine({
      className: 'credit',
      description: creditText,
      price: -creditAmount,
      showNumberForZero: true,
    })
  }

  renderTotal() {
    const totalText = i18n.t('pages.cart.total_text')
    const description = (
      <span>
        {totalText}
        <span className="currency">
          {this.props.summarizable.payloadCurrency}
        </span>
      </span>
    )
    return this.renderLine({
      className: 'total footer',
      description,
      price: this.props.summarizable.total,
      text: undefined,
      showNumberForZero: true,
    })
  }

  renderProceedButton() {
    const { canProceed, proceedAction, proceedText } = this.props

    if (canProceed === undefined || proceedAction === undefined) {
      return null
    }

    const buttonType = this.props.proceedButtonType || 'progress'

    return (
      <div className="proceed">
        <ReCaptcha onClick={proceedAction}>
          {(proceedActionWithReCaptcha) => (
            <ResponsiveButton
              data-testid="proceed-to-checkout"
              type={buttonType}
              disabled={!canProceed}
              onClick={proceedActionWithReCaptcha}
            >
              {proceedText}
            </ResponsiveButton>
          )}
        </ReCaptcha>
      </div>
    )
  }

  renderAffirmMarketing() {
    const {
      summarizable: { total },
      displayAffirm,
    } = this.props
    return displayAffirm ? (
      <AffirmMarketing cartTotal={total} textAlign="right" hideLearnMore />
    ) : null
  }

  renderCartLink() {
    const { renderCartLink } = this.props
    if (!renderCartLink) return null

    return (
      <ViewCartButton
        glyphSide="right"
        glyph="chevron-right"
        type="plain"
        to="/cart"
      >
        {i18n.t('components.order_summary.view_modify_cart')}
      </ViewCartButton>
    )
  }

  render() {
    const orderSummaryText = i18n.t('pages.cart.order_summary_text')
    const { className } = this.props

    return (
      <div className={classNames('amp-order-summary', className)}>
        <div className="heading">{orderSummaryText}</div>
        <div
          className={classNames('totals', {
            'bottom-border': !this.props.applyPromosBox,
          })}
        >
          {this.renderSubtotal()}
          {this.renderSavingsAmount()}
          {this.renderTaxesAndFees()}
          {this.renderFoundationDonation()}
        </div>
        {this.props.applyPromosBox}
        {this.props.children}
        {this.renderNetTotalAndCreditValue()}
        {this.renderTotal()}
        {this.renderAffirmMarketing()}
        {this.props.checkTerms && (
          <ConsentCheckbox
            checked={this.props.termsAccepted}
            alert={this.props.alertTerms}
            onChange={this.props.checkTerms}
          >
            {this.props.termsContent}
          </ConsentCheckbox>
        )}
        {this.props.minorConsentRequired && (
          <ConsentCheckbox
            checked={this.props.minorConsentAccepted}
            alert={this.props.minorConsentAlerted}
            onChange={this.props.toggleMinorConsent}
          >
            {this.props.minorConsentContent}
          </ConsentCheckbox>
        )}
        {this.renderProceedButton()}
        {this.renderCartLink()}
        {this.props.renderPurchaseDisclaimer && (
          <Disclaimer>
            <ContentBlock>{i18n.t('pages.review.disclaimer')}</ContentBlock>
          </Disclaimer>
        )}
        {this.props.renderSummitSupport && <StyledSummitSupportSummary />}
      </div>
    )
  }
}
